import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import LogoEdunara from "assets/img/Edunara-Logo.png";
import IconEdunara from "assets/img/Edunara-Icon.png";
import { useAuth } from "contexts/AuthContext";
import { useTranslation } from "react-i18next";

// Icons (inline SVG)
const HomeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);
const UsersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
  </svg>
);
const BuildingIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
    <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
  </svg>
);
const PackageIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"/>
    <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
);
const ActivityIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
  </svg>
);
const SettingsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14"/>
    <path d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
  </svg>
);
const HelpIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);
const ChevronLeftIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);
const ChevronRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

function Avatar({ name }) {
  const initials = (name || "U")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const colors = ["#4F46E5","#0EA5E9","#10B981","#F59E0B","#EF4444","#8B5CF6"];
  const bg = colors[(name || "U").charCodeAt(0) % colors.length];
  return (
    <div className="lms-avatar lms-avatar-sm" style={{ background: bg }}>
      {initials}
    </div>
  );
}

export default function Sidebar({ collapsed, onToggle }) {
  const { t } = useTranslation();
  const location = useLocation();
const { user } = useAuth();

  const userName = user?.full_name || user?.name || user?.username || "Admin";
  const userEmail = user?.email || "";

  const isActive = (path) => location.pathname === "/admin" + path;
  const isActiveGroup = (...paths) => paths.some((p) => location.pathname.startsWith("/admin" + p));

  return (
    <aside className="lms-sidebar">
      {/* Header */}
      <div className="lms-sidebar-header">
        <div className="lms-sidebar-logo">
          <img className="logo-full" src={LogoEdunara} alt="Edunara" />
          <img className="logo-icon" src={IconEdunara} alt="Edunara" />
        </div>
        <button className="lms-sidebar-toggle" onClick={onToggle} aria-label="Toggle sidebar">
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </button>
      </div>

      {/* Nav */}
      <nav className="lms-sidebar-nav">
        {/* Overview */}
        <div className="lms-nav-section-title">{t("Overview")}</div>
        <NavLink
          to="/admin/dashboard"
          className={`lms-nav-item ${isActive("/dashboard") ? "active" : ""}`}
        >
          <HomeIcon />
          <span>{t("Dashboard")}</span>
        </NavLink>

        {/* Management */}
        <div className="lms-nav-section-title">{t("Management")}</div>
        <NavLink
          to="/admin/user-management"
          className={`lms-nav-item ${isActive("/user-management") ? "active" : ""}`}
        >
          <UsersIcon />
          <span>{t("User Management")}</span>
        </NavLink>
        <NavLink
          to="/admin/master-tenant"
          className={`lms-nav-item ${isActiveGroup("/master-tenant", "/create-tenant", "/tenant-detail", "/edit-tenant", "/tenant-menu-setting") ? "active" : ""}`}
        >
          <BuildingIcon />
          <span>{t("Master Tenant")}</span>
        </NavLink>
        <NavLink
          to="/admin/master-package"
          className={`lms-nav-item ${isActive("/master-package") ? "active" : ""}`}
        >
          <PackageIcon />
          <span>{t("Master Package")}</span>
        </NavLink>

        {/* System */}
        <div className="lms-nav-section-title">{t("System")}</div>
        <NavLink
          to="/admin/activity-log"
          className={`lms-nav-item ${isActive("/activity-log") ? "active" : ""}`}
        >
          <ActivityIcon />
          <span>{t("Activity Log")}</span>
        </NavLink>
        <NavLink
          to="/admin/settings"
          className={`lms-nav-item ${isActive("/settings") ? "active" : ""}`}
        >
          <SettingsIcon />
          <span>{t("Settings")}</span>
        </NavLink>
        <NavLink
          to="/admin/help-support"
          className={`lms-nav-item ${isActive("/help-support") ? "active" : ""}`}
        >
          <HelpIcon />
          <span>{t("Help & Support")}</span>
        </NavLink>
      </nav>

      {/* Footer */}
      <div className="lms-sidebar-footer">
        <div className="lms-sidebar-user">
          <Avatar name={userName} />
          <div className="lms-sidebar-user-info">
            <div className="lms-sidebar-user-name">{userName}</div>
            <div className="lms-sidebar-user-role">{userEmail}</div>
          </div>
        </div>
      </div>
    </aside>
  );
}

// Keep responsive export for backward compat
export function SidebarResponsive() { return null; }
